import React, { useState, useEffect, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, LayoutGrid, Zap, ArrowDownUp, Timer } from 'lucide-react';
import itemService from '../../services/itemService';
import catalogService from '../../services/catalogService'; 
import ProductCard from '../../components/common/ProductCard';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl } from '../../utils/helpers';

// 👉 TỐI ƯU 1: Tách Timer ra Component riêng biệt. 
// Chỉ component này re-render mỗi giây, không ảnh hưởng đến danh sách sản phẩm.
const FlashSaleTimer = memo(({ endTime, onExpire }) => {
    const [timeLeft, setTimeLeft] = useState({ h: '00', m: '00', s: '00' });

    useEffect(() => {
        if (!endTime) return;
        
        const timer = setInterval(() => {
            const distance = endTime - new Date().getTime();

            if (distance <= 0) {
                clearInterval(timer);
                setTimeLeft({ h: '00', m: '00', s: '00' });
                if (onExpire) onExpire();
            } else {
                const h = Math.floor(distance / (1000 * 60 * 60));
                const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const s = Math.floor((distance % (1000 * 60)) / 1000);
                
                setTimeLeft({
                    h: h.toString().padStart(2, '0'),
                    m: m.toString().padStart(2, '0'),
                    s: s.toString().padStart(2, '0')
                });
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [endTime, onExpire]);

    return (
        <div className="flex items-center gap-2 text-2xl font-black text-white">
            <div className="bg-white text-tnc-orange min-w-[3rem] w-auto px-2 h-12 flex items-center justify-center rounded-lg shadow-inner">{timeLeft.h}</div>
            <span className="text-orange-200 animate-pulse">:</span>
            <div className="bg-white text-tnc-orange w-12 h-12 flex items-center justify-center rounded-lg shadow-inner">{timeLeft.m}</div>
            <span className="text-orange-200 animate-pulse">:</span>
            <div className="bg-white text-tnc-orange w-12 h-12 flex items-center justify-center rounded-lg shadow-inner">{timeLeft.s}</div>
        </div>
    );
});

const FlashSalePage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState('default');
    
    const [systemEndTime, setSystemEndTime] = useState(null);
    const [isSaleEnded, setIsSaleEnded] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    useEffect(() => {
        setCurrentPage(1);
    }, [sortOrder]);

    useEffect(() => {
        document.title = "Giờ Vàng Deal Sốc | Thành Nam PC";
        
        const fetchFlashSaleData = async () => {
            setLoading(true);
            try {
                const [res, configRes] = await Promise.all([
                    itemService.getPublicItems().catch(() => []),
                    catalogService.getSystemConfig().catch(() => ({}))
                ]);

                const rawData = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                const conf = configRes?.data || configRes || {};
                
                const now = new Date().getTime();
                const endTime = conf.flash_sale_end ? new Date(conf.flash_sale_end).getTime() : null;

                if (endTime && endTime < now) {
                    setIsSaleEnded(true);
                    setProducts([]);
                } else {
                    const validItems = rawData.filter(p => p.itemFlashSalePrice > 0 && p.itemFlashSaleQuantity > 0);
                    setProducts(validItems);
                    setSystemEndTime(endTime);
                    if (!endTime && validItems.length === 0) setIsSaleEnded(true);
                }
            } catch (error) {
                console.error("Lỗi lấy dữ liệu Flash Sale:", error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchFlashSaleData();
    }, []);

    const handleSaleExpire = () => {
        setIsSaleEnded(true);
        setProducts([]);
    };

    const displayedProducts = useMemo(() => {
        let result = [...products];
        if (sortOrder === 'price_asc') {
            result.sort((a, b) => a.itemFlashSalePrice - b.itemFlashSalePrice);
        } else if (sortOrder === 'price_desc') {
            result.sort((a, b) => b.itemFlashSalePrice - a.itemFlashSalePrice);
        } else {
            result.sort((a, b) => {
                const prioA = a.priority || 0;
                const prioB = b.priority || 0;
                if (prioA !== prioB) return prioB - prioA;
                return b.itemId - a.itemId; 
            });
        }
        return result;
    }, [products, sortOrder]);

    const totalPages = Math.ceil(displayedProducts.length / itemsPerPage) || 1;
    
    const paginatedProducts = useMemo(() => {
        return displayedProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    }, [displayedProducts, currentPage]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    const getPaginationRange = () => {
        const delta = 2; 
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }
        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");
        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);
        return range;
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            <main className="flex-grow pb-12">
                <div className="bg-gradient-to-r from-orange-50 to-white border-b border-orange-200/60">
                    <div className="container mx-auto px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                        <Link to="/" className="hover:text-tnc-orange transition-colors">Trang chủ</Link>
                        <ChevronRight size={14} />
                        <span className="font-bold text-tnc-orange">Giờ Vàng Deal Sốc</span>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-tnc-orange-dark via-tnc-orange to-tnc-orange-light py-8 shadow-inner">
                    <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm animate-pulse">
                                <Zap size={40} className="text-yellow-300 fill-yellow-300" />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-wide uppercase drop-shadow-md">
                                    Giờ Vàng Deal Sốc
                                </h1>
                                <p className="text-orange-100 mt-1 font-medium text-lg">
                                    {isSaleEnded ? "Chương trình đã kết thúc!" : "Săn sale chớp nhoáng - Số lượng có hạn!"}
                                </p>
                            </div>
                        </div>

                        {!isSaleEnded && systemEndTime && (
                            <div className="flex items-center gap-4 bg-black/20 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20 shadow-lg">
                                <Timer size={24} className="text-white animate-spin-slow" />
                                <FlashSaleTimer endTime={systemEndTime} onExpire={handleSaleExpire} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="container mx-auto px-4 mt-8">
                    <div className="bg-gradient-to-r from-orange-50 to-white p-4 rounded-xl shadow-sm border border-orange-200/60 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <h2 className="font-bold text-gray-800 flex items-center gap-2">
                            Hiển thị <span className="text-tnc-orange text-lg">{displayedProducts.length}</span> sản phẩm
                        </h2>

                        <div className="flex items-center gap-2 border border-orange-200/60 rounded-lg px-3 py-1.5 bg-white hover:border-tnc-orange focus-within:border-tnc-orange transition-colors w-full sm:w-auto shadow-sm">
                            <ArrowDownUp size={14} className="text-gray-500" />
                            <select 
                                value={sortOrder} 
                                onChange={(e) => setSortOrder(e.target.value)} 
                                className="bg-transparent outline-none cursor-pointer text-gray-700 font-medium w-full"
                            >
                                <option value="default">Sắp xếp: Nổi bật</option>
                                <option value="price_asc">Giá: Thấp đến Cao</option>
                                <option value="price_desc">Giá: Cao đến Thấp</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="bg-gray-200 animate-pulse h-80 rounded-xl"></div>
                            ))}
                        </div>
                    ) : displayedProducts.length > 0 ? (
                        <>
                            {/* 👉 TỐI ƯU 2: Xóa class "flex" khỏi grid để tránh lỗi giao diện Tailwind */}
                            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
                                {paginatedProducts.map(product => (
                                    <ProductCard 
                                        key={product.itemId} 
                                        product={product} 
                                        getImageUrl={getImageUrl} 
                                        adminSpecs={[]} 
                                    />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 border-t border-orange-200/60 pt-6">
                                    <div className="text-sm text-gray-500 font-medium">
                                        Hiển thị <strong className="text-gray-900">{(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, displayedProducts.length)}</strong> trên tổng số <strong className="text-gray-900">{displayedProducts.length}</strong> sản phẩm
                                    </div>

                                    <div className="flex flex-wrap items-center justify-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => handlePageChange(currentPage - 1)} 
                                                disabled={currentPage === 1}
                                                className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                            >
                                                <ChevronLeft size={16} /> <span className="hidden sm:inline">Trang trước</span>
                                            </button>

                                            <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] md:max-w-none scrollbar-hide">
                                                {getPaginationRange().map((page, index) => {
                                                    if (page === "...") {
                                                        return <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 font-bold">...</span>;
                                                    }
                                                    return (
                                                        <button 
                                                            key={index} 
                                                            onClick={() => handlePageChange(page)}
                                                            className={`w-10 h-10 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                                ${currentPage === page 
                                                                    ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                                    : 'bg-white text-gray-600 border-orange-200 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    )
                                                })}
                                            </div>

                                            <button 
                                                onClick={() => handlePageChange(currentPage + 1)} 
                                                disabled={currentPage === totalPages}
                                                className="px-3.5 py-2 rounded-lg border border-orange-200 bg-white text-gray-600 font-bold hover:bg-orange-50 hover:text-tnc-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                                            >
                                                <span className="hidden sm:inline">Trang sau</span> <ChevronRight size={16} />
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                            <span className="text-sm text-gray-500 font-medium">Đến trang:</span>
                                            <input 
                                                key={`flash-sale-page-${currentPage}`}
                                                type="number" min={1} max={totalPages} defaultValue={currentPage}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        const page = parseInt(e.target.value);
                                                        if (page >= 1 && page <= totalPages) handlePageChange(page);
                                                        else e.target.value = currentPage;
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const page = parseInt(e.target.value);
                                                    if (page >= 1 && page <= totalPages && page !== currentPage) handlePageChange(page);
                                                    else e.target.value = currentPage;
                                                }}
                                                className="w-16 h-10 px-2 text-center border border-orange-200 rounded-lg outline-none text-sm font-bold text-gray-700 focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-all"
                                                title="Nhập số trang và nhấn Enter"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-gradient-to-b from-orange-50 to-white py-20 rounded-2xl shadow-sm text-center border border-orange-200/60 flex flex-col items-center">
                            <div className="bg-white p-6 rounded-full mb-4 shadow-sm border border-orange-100">
                                <LayoutGrid className="text-tnc-orange w-12 h-12" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-700">Chương trình đã kết thúc!</h3>
                            <p className="text-gray-500 mt-2 max-w-sm">
                                Hiện tại không có sản phẩm nào trong chương trình Flash Sale hoặc các sản phẩm đã được bán hết. Vui lòng quay lại sau!
                            </p>
                            <Link to="/" className="mt-6 bg-tnc-orange text-white font-bold py-2.5 px-8 rounded-lg hover:bg-tnc-orange-hover transition-colors shadow-sm">
                                Về trang chủ
                            </Link>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default FlashSalePage;